import React from "react";
import {
  Mail,
  Phone,
  Globe,
  MapPin,
  Calendar,
  Download,
  Share2,
} from "lucide-react";
import { TransformedProfileData } from "@/types/publicProfile";
import { Section } from "./Section";

interface ContactSectionProps {
  profileData: TransformedProfileData;
  onDownloadVCard: () => void;
  onShare: () => void;
}

export const ContactSection: React.FC<ContactSectionProps> = ({
  profileData,
  onDownloadVCard,
  onShare,
}) => {
  const data = profileData;

  return (
    <Section
      id="contact"
      title="Save to Contact"
      icon={<Mail className="h-5 w-5" />}
    >
      <div className="grid gap-8 lg:grid-cols-3">
        {/* Contact Methods */}
        <div className="lg:col-span-2 space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <a
              href={`mailto:${data.contactInfo.email}`}
              className="group flex items-center gap-4 p-4 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 hover:border-blue-300 dark:hover:border-blue-600 transition-all duration-300 hover:shadow-lg"
            >
              <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                <Mail className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  Email
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                  {data.contactInfo.email}
                </p>
              </div>
              <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              </div>
            </a>

            {data.contactInfo.phone && (
              <a
                href={`tel:${data.contactInfo.phone}`}
                className="group flex items-center gap-4 p-4 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 hover:border-green-300 dark:hover:border-green-600 transition-all duration-300 hover:shadow-lg"
              >
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center">
                  <Phone className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    Phone
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {data.contactInfo.phone}
                  </p>
                </div>
                <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                </div>
              </a>
            )}

            {data.contactInfo.website && (
              <a
                href={data.contactInfo.website}
                target="_blank"
                rel="noreferrer"
                className="group flex items-center gap-4 p-4 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 hover:border-purple-300 dark:hover:border-purple-600 transition-all duration-300 hover:shadow-lg"
              >
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center">
                  <Globe className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    Website
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                    {data.contactInfo.website}
                  </p>
                </div>
                <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                </div>
              </a>
            )}

            <div className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-2xl border border-gray-200 dark:border-gray-700">
              <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center">
                <MapPin className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  Location
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {data.contactInfo.location}
                </p>
              </div>
            </div>

            {data.contactInfo.timezone && (
              <div className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-2xl border border-gray-200 dark:border-gray-700">
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl flex items-center justify-center">
                  <Calendar className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    Timezone
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {data.contactInfo.timezone}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Action Card */}
        <div className="bg-gradient-to-br from-gray-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-blue-900/20 rounded-2xl border border-gray-200 dark:border-gray-800 p-6">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Mail className="h-8 w-8 text-white" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Let's Work Together
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
              {data.contactInfo.availableForWork
                ? "I'm currently available for new projects and collaborations. Let's discuss how we can work together!"
                : "While I'm not taking on new projects right now, I'm always happy to connect and discuss future opportunities."}
            </p>
          </div>

          <div className="space-y-3">
            <button
              onClick={onDownloadVCard}
              className="w-full group relative bg-gray-900 dark:bg-white text-white dark:text-gray-900 px-4 py-3 rounded-xl font-medium transition-all duration-300 hover:shadow-lg hover:scale-[1.02] flex items-center justify-center gap-2"
            >
              <Download className="h-5 w-5" />
              Save Contact
              <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-blue-600/0 to-purple-600/0 group-hover:from-blue-600/20 group-hover:to-purple-600/20 transition-all duration-300"></div>
            </button>

            <button
              onClick={onShare}
              className="w-full bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 px-4 py-3 rounded-xl font-medium transition-all duration-300 hover:shadow-md hover:scale-[1.02] flex items-center justify-center gap-2"
            >
              <Share2 className="h-5 w-5" />
              Share Profile
            </button>

            {data.contactInfo.calendlyUrl && (
              <a
                href={data.contactInfo.calendlyUrl}
                target="_blank"
                rel="noreferrer"
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-4 py-3 rounded-xl font-medium transition-all duration-300 hover:shadow-lg hover:scale-[1.02] flex items-center justify-center gap-2"
              >
                <Calendar className="h-5 w-5" />
                Schedule a Call
              </a>
            )}
          </div>

          {/* Availability indicator */}
          <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-center gap-2 text-sm">
            <div
              className={`w-2 h-2 rounded-full ${
                data.contactInfo.availableForWork
                  ? "bg-green-500"
                  : "bg-orange-500"
              }`}
            ></div>
            <span className="text-gray-600 dark:text-gray-400">
              {data.contactInfo.availableForWork
                ? "Available for work"
                : "Currently unavailable"}
            </span>
          </div>
        </div>
      </div>
    </Section>
  );
};
